import { useState } from "react";
import { useLoaderData, useNavigate, useParams } from "react-router-dom";
import { Modal, Form } from "react-bootstrap";
import { Button } from "./Controls/Button";
import { AddSubtract } from "./Controls/AddSubtract";
import { Facility } from "./Data/ApiTypes";
import { createApiService } from "./shared/apiConfig";
import { ParkingValidationRequestApi } from "./shared/api/axios-client";
import { toDatetimeLocalString } from "./Data/Date";
import { toast } from "react-toastify";
import { CarFrontFill, CheckCircleFill } from "react-bootstrap-icons";

const MS_PER_H = 60 * 60 * 1000;

function datetimeLocalPlusOneHour( datetimeLocal: string ): string {
    const d = new Date( datetimeLocal );
    if ( Number.isNaN( d.getTime() ) ) return datetimeLocal;
    d.setTime( d.getTime() + MS_PER_H );
    return toDatetimeLocalString( d );
}

function datetimeLocalMinusOneHour( datetimeLocal: string ): string {
    const d = new Date( datetimeLocal );
    if ( Number.isNaN( d.getTime() ) ) return datetimeLocal;
    d.setTime( d.getTime() - MS_PER_H );
    return toDatetimeLocalString( d );
}

export function ValidationRequestPage() {
    const { facilityId, rateProgramId } = useParams();
    const facility = useLoaderData() as Facility;
    const nav = useNavigate();

    const [showIntroModal, setShowIntroModal] = useState(true);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [formData, setFormData] = useState({
        start: "", 
        end: "",
        spaces: 1,
        name: "",
        email: ""
    });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleStartChange = ( e: React.ChangeEvent<HTMLInputElement> ) => {
        const value = e.target.value;
        setFormData( prev => {
            if ( !value ) return { ...prev, start: value };
            let start = value;
            if ( prev.end ) {
                const maxStart = datetimeLocalMinusOneHour( prev.end );
                if ( new Date( start ).getTime() > new Date( maxStart ).getTime() )
                    start = maxStart;
            }
            const minEnd = datetimeLocalPlusOneHour( start );
            const endTooEarly = prev.end && new Date( prev.end ).getTime() < new Date( minEnd ).getTime();
            return endTooEarly ? { ...prev, start, end: "" } : { ...prev, start };
        } );
    };

    const handleEndChange = ( e: React.ChangeEvent<HTMLInputElement> ) => {
        const value = e.target.value;
        setFormData( prev => {
            if ( !value ) return { ...prev, end: value };
            if ( !prev.start ) return { ...prev, end: value };
            const minEnd = datetimeLocalPlusOneHour( prev.start );
            if ( new Date( value ).getTime() < new Date( minEnd ).getTime() )
                return { ...prev, end: minEnd };
            return { ...prev, end: value };
        } );
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if ( formData.start && formData.end ) {
            const s = new Date( formData.start ).getTime();
            const en = new Date( formData.end ).getTime();
            if ( !Number.isFinite( s ) || !Number.isFinite( en ) || en - s < MS_PER_H ) {
                toast.error( "End must be at least one hour after start." );
                return;
            }
        }

        try {
            const validationApi = createApiService(ParkingValidationRequestApi);
            var response = await validationApi.submitParkingValidationRequest({
                serverRequestsSubmitParkingValidationRequest : {
                facilityId: Number(facilityId),
                startDateTime: formData.start ? (new Date(formData.start).toISOString() as unknown as object) : undefined,
                endDateTime: formData.end ? (new Date(formData.end).toISOString() as unknown as object) : undefined,
                numberOfSpaces: Number(formData.spaces),
                parkerName: formData.name,
                parkerEmail: formData.email,
                rateProgramId: Number(rateProgramId)
            }
            });
            if (response.data.error) {
                toast.error(response.data.error);
                return;
            }   
            setShowConfirmModal(true);
        } catch {
            toast.error("Error submitting validation request.");
        }
    };

    const todayMidnight = new Date();
    todayMidnight.setHours(0, 0, 0, 0);
    const minStartDate = toDatetimeLocalString(todayMidnight);
    const minEndDate = formData.start
        ? datetimeLocalPlusOneHour( formData.start )
        : datetimeLocalPlusOneHour( minStartDate );
    const maxStartDate = formData.end ? datetimeLocalMinusOneHour( formData.end ) : undefined;

    return (
        <>
        <Modal centered show={showIntroModal} onHide={() => setShowIntroModal(false)}>
            <Modal.Header closeButton>
                <Modal.Title><CarFrontFill className="me-1 icon-shift-up" /> Request Parking</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="mb-3">
                   
                    <p className="mb-2">
                        Complete a quick form to request parking at <strong>{facility?.name}</strong>.
                    </p>
                    <div className="bg-light p-3 rounded">
                        <small className="text-muted">
                            <strong>What you'll need:</strong>
                            <ul className="mb-0 mt-1">
                                <li>Visit details (date, time, number of spaces)</li>
                                <li>Contact information</li>
                            </ul>
                        </small>
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button className="btn-main" onClick={() => setShowIntroModal(false)}>
                    Start Request
                </Button>
            </Modal.Footer>
        </Modal>

            <div className="container mt-4">
                <h3 className="mb-4 mx-2">Request Parking at <b>{facility?.name}</b></h3>
              
                <div className="border rounded-3 p-4 shadow">
                    <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                <Form.Label>Start Date & Time</Form.Label>
                <Form.Control
                    type="datetime-local"
                    name="start"
                    value={formData.start}
                    onChange={handleStartChange}
                    min={minStartDate}
                    max={maxStartDate}
                    required
                />
                </Form.Group>

                <Form.Group className="mb-3">
                <Form.Label>End Date & Time</Form.Label>
                <Form.Control
                    type="datetime-local"
                    name="end"
                    value={formData.end}
                    onChange={handleEndChange}
                    min={minEndDate}
                    required
                />
                </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Number of Spaces</Form.Label>
                        <div className="mt-2">
                            <AddSubtract
                                quantity={formData.spaces}
                                set={(n: number) => setFormData(prev => ({ ...prev, spaces: n }))}
                            />
                        </div>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Name</Form.Label>
                        <Form.Control
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                        />
                    </Form.Group>

                <Button className="btn-main" type="submit" disabled={showConfirmModal}>
                    {showConfirmModal ? "Request submitted" : "Submit Request"}
                </Button>
                </Form>
                </div>
            </div>

            <Modal centered show={showConfirmModal} onHide={() => nav("/")}>
                <Modal.Header closeButton>
                    <Modal.Title><CheckCircleFill className="me-1 icon-shift-up" /> Request Submitted</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <b>Your request has been submitted successfully.</b>
                    <div className="mt-2 text-muted">Look out for an email with instructions on how to proceed after we review.</div> 
                </Modal.Body>
                <Modal.Footer>
                    <Button className="btn-main" onClick={() => nav("/")}>OK</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}
